import { ctx } from '../canvas.js';
import { state } from '../state.js';
import { ACCESSORIES, BLINK_MS, BLINK_PERIOD_MS, BREATH_HZ, FIST_DX, FIST_DY, SHIELD_H, SHIELD_POINT, SHIELD_W, SPRING_DANCE_LEAN, SPRING_DANCE_SWAY, SPRING_DANCE_SWAYS_PER_SECOND, SPRING_DANCE_UP, SPRING_H, SPRING_W, SPECIES } from '../config.js';
import { anticPose } from '../antics.js';
import { rr, pixelRect, clamp } from '../utils.js';
import { leapOffset } from '../leap.js';

/** Ink for eyes, nose and mouth. */
const INK = '#150f0a';

/**
 * The species and the accessories a figure is standing in.
 *
 * A figure can bring its own clothes, and the opening scene does: it shows off the
 * wardrobe rather than wearing the players'. The two sides' looks belong to the
 * match, so a demo dressed as the player would be changing the player's character
 * every time it walked on.
 */
function lookFor(p, idx) {
  if (p && p.look) return p.look;

  const looks = state.looks || [];
  return looks[idx] || { species: idx, worn: {} };
}

function speciesFor(p, idx) {
  return SPECIES[lookFor(p, idx).species] || SPECIES[0];
}

/** The accessories a figure is wearing, as specs. Unknown ids are ignored. */
function wornFor(p, idx) {
  const worn = lookFor(p, idx).worn || {};

  return Object.values(worn)
    .map(id => ACCESSORIES.find(a => a.id === id))
    .filter(Boolean);
}

/** Which way this side faces. Boxes are authored facing right and mirrored. */
/**
 * Which way a side is looking: +1 to the right, -1 to the left.
 *
 * This was `idx === 0 ? 1 : -1` for a long time, and it is the single place that
 * decides the mirroring for everything — the arms, the accessories, the fist, and
 * the hitbox, which is built from the same boxes the drawing fills in. Now that it
 * is state, an ult that moves somebody to the other side of the skyline only has to
 * turn them round, and the picture and the hit test turn together.
 */
export function facingOf(idx) {
  const facing = state.facing;
  if (facing && facing[idx]) return facing[idx];

  return idx === 0 ? 1 : -1;
}

/**
 * One box, placed in world coordinates. Used by the drawing AND by the hit test,
 * which is the whole point: there is one description of a hat in this codebase,
 * so it cannot be somewhere other than where it can be hit.
 */
function partRect(part, cx, cy, dir) {
  return {
    x: Math.round(dir > 0 ? cx + part.x : cx - part.x - part.w),
    y: Math.round(cy + part.y),
    w: part.w,
    h: part.h,
    c: part.c,
  };
}

/**
 * Where the throwing fist sits — also the launch point of the banana.
 *
 * It follows the leap, so a shot thrown from the top of one starts where the hand
 * actually is. Without that the banana would set off from the roof while the
 * gorilla is forty pixels above it — the same lie the hitboxes stopped telling
 * when the arm and the test were made to read one pose.
 */
export function gorillaFistPos(p, idx) {
  const dir = facingOf(idx);
  // The ride is in here for the same reason the leap is: a shot has to start where the hand
  // actually is. It is added rather than subtracted because a `lift` is negative when it is
  // upwards (see springRide), which is the one place in this file where the sign is easy to get
  // backwards — and getting it backwards put the launch point nine pixels below the roof while
  // the gorilla was drawn nine above it.
  //
  // The sway, on purpose, is not: eleven pixels of a dance, and a launch point that wanders
  // sideways with it is a launch point that can wander into the taller tower next door. The
  // hand is drawn where the dance puts it and the banana leaves from there — one of the two has
  // to give, and it is not the thing on the roof.
  return { x: p.x + dir * FIST_DX, y: p.y + FIST_DY - leapOffset(idx) + springRide(idx).lift };
}

/**
 * The spring, as a ride: how far it is holding him up, how far it has folded, and how far it is
 * carrying him to one side.
 *
 * A spring is not a fence, it is the clown on the lid of the box, and the gorilla is the figure
 * on top of it. He climbs on when it is planted and rides it for the turn it lasts — which is
 * exactly as long as it is standing, so the dance is the spring's own clock and needs nothing to
 * start or stop it.
 *
 * One beat for the three of them, and that is the whole of the picture: `lean` is the spring
 * folded over, `sway` is where its plate is when it is, and `stretch` is how straight it is
 * standing — so the plate is always exactly under his feet. His feet are at SPRING_H + stretch
 * above the roof, and the plate is drawn at SPRING_H + stretch above the roof, which is the
 * arithmetic that makes him a rider rather than a floater.
 *
 * All of it goes into the pose, which is the only reason the dance is safe to write: the drawing
 * and the hit test read the same pose, so a gorilla riding a spring is a gorilla whose body is
 * riding it, and a banana aimed at where he was a moment ago goes under him. That is what a
 * bobblehead is for, and it is a real defence rather than a decoration.
 */
function springRide(idx) {
  const spring = state.spring;
  if (!spring || spring.idx !== idx) return { lift: 0, sway: 0, bend: 0, stretch: 0 };

  const beat = (state.clock - spring.at) / 1000 * SPRING_DANCE_SWAYS_PER_SECOND * Math.PI * 2;
  const bend = Math.sin(beat);
  // Straight up at the top of the beat, folded over at the ends of it, and never both.
  const stretch = (1 - Math.abs(bend)) * SPRING_DANCE_UP;

  return {
    // Negative is up — the convention the pose is read in, both by the drawing and by the hit
    // test. This was positive for as long as the spring has existed, which sank the gorilla nine
    // pixels into the roof and left the spring drawn across his knees: the plate has to be where
    // his feet are, and his feet are SPRING_H + stretch ABOVE the roof, so his `lift` is that
    // number with a minus in front of it.
    lift: -(SPRING_H + stretch),
    sway: bend * SPRING_DANCE_SWAY,
    bend,
    stretch,
  };
}

/**
 * Where the head is, in canvas coordinates: the anchor for hats, speech bubbles
 * and anything else that has to sit on the gorilla. It lives here, next to the
 * drawing that computes the same point, so the two cannot drift apart.
 */
export function gorillaHead(p, idx) {
  const q = gorillaPose(p, idx);
  const s = q.shape;

  return {
    x: Math.round(p.x + q.jitter) + q.head.x,
    y: Math.round(p.y + q.lift) + s.headY + q.bob + q.headBob + q.head.y,
    rx: s.headRx,
    ry: s.headRy,
  };
}

/**
 * Everything about how the gorilla looks this frame: the idle motion, plus
 * whatever antic is running.
 *
 * The drawing and the hit test both read this, which is the whole point. The
 * arms spend most of their time somewhere other than the neutral stance — an
 * antic lasts 1.5-2s out of every 3-8s — so a target built on the neutral pose
 * spent a third of the game registering hits on thin air.
 */
export function gorillaPose(p, idx) {
  const species = speciesFor(p, idx);
  const { bob, head: headBob, sway, blink } = idleMotion(idx);
  const antic = anticPose(idx, p);
  const s = species.shape;

  // Where the forward arm hangs when nothing is going on — and therefore also where a
  // shield is held, since gorillaShieldRect anchors on `p.x + dir * FIST_DX` and the
  // standing stance puts the fist on exactly those numbers (30 and -6).
  const restThrow = { x: idx ? -s.fistDx : s.fistDx, y: s.fistDy };

  // A shield is held: the arm goes to the shield rather than the shield following the arm.
  //
  // Without this the antic carries the hand up and away — an antic runs 1.5-2s out of
  // every 3-8 — and leaves the shield standing on its own beside a gorilla with nothing
  // in his hand, which is exactly what it looked like. Pinning the fist pins it for the
  // drawing AND for the hit test, because both read this pose: the arm that is painted is
  // still the arm that counts. The shield's own rectangle does not move, so what it blocks
  // is unchanged.
  const holding = !!state.shield && state.shield.idx === idx;
  const ride = springRide(idx);

  return {
    species,
    shape: s,
    worn: wornFor(p, idx),
    bob,
    headBob,
    blink,
    jitter: (antic.jitter || 0) + ride.sway,
    // The leap rides on top of whatever the antic is doing, so the drawing and
    // the hit test climb together: a gorilla in the air cannot be hit where he no
    // longer is.
    lift: (antic.lift || 0) + leapOffset(idx) + ride.lift,
    // The head leans into the swing, which is what says the figure on the plate is a rider
    // rather than a statue being slid sideways: the rest of him follows the plate, and the
    // head is the part of a body that goes last. In the pose, so the hat and the fists and
    // the hit test all go with it.
    head: { x: (antic.head ? antic.head.x : 0) + ride.bend * SPRING_DANCE_LEAN, y: antic.head ? antic.head.y : 0 },
    free: antic.freeFist || { x: (idx ? s.freeDx : -s.freeDx) + sway, y: s.freeDy },
    thrown: holding ? restThrow : (antic.throwFist || restThrow),
    eyes: antic.eyes,
    mouth: antic.mouth || 0,
    zzz: !!antic.zzz,
  };
}

// --- hit test --------------------------------------------------------------

// Measured off the silhouette drawGorilla() actually paints, not off a circle
// around the anchor. The sprite is 68x61 px, so a 22px radius covered only 60%
// of it and a banana sank three frames into the gorilla's head before anything
// happened.
//
// Every number now comes from the species' shape rather than from a constant
// here, and the accessories are tested against the very boxes they are drawn
// from. A chimp is a smaller target than a rhino, and a top hat is a bigger one —
// which is the honest consequence of "what you see is what you can hit".

/** Slack on the drawn edge (px). A little forgiveness, not a whole arm. */
const HIT_SLACK = 1.5;

/** Shortest distance from a point to a line segment. */
function distToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? clamp(((px - ax) * dx + (py - ay) * dy) / len2, 0, 1) : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** True when a body-local point is inside one of an accessory's boxes. */
function inPart(dx, dy, part, sign) {
  const x0 = sign > 0 ? part.x : -part.x - part.w;

  return dx >= x0 - HIT_SLACK && dx <= x0 + part.w + HIT_SLACK
    && dy >= part.y - HIT_SLACK && dy <= part.y + part.h + HIT_SLACK;
}

/**
 * Inside a rounded rectangle, the same shape rr() draws: the straight body with
 * the corners cut by a circle of radius r.
 *
 * The hit test has to make the same cut. A plain rectangle here is how a banana
 * ends up exploding in the air beside a shoulder, because the corner that was
 * rounded away is still inside the box. The distance outside the inner rectangle
 * is measured per axis and compared against the radius, so a point past the
 * corner in both axes has to be that much closer to the middle.
 */
function inRoundedRect(dx, dy, halfW, top, bottom, r) {
  const ox = Math.max(0, Math.abs(dx) - (halfW - r));
  const oy = Math.max(0, dy - (bottom - r), top + r - dy);

  return Math.hypot(ox, oy) <= r;
}

/**
 * True when (px, py) is inside the character <em>as it is drawn right now</em>.
 * World coordinates: the caller passes the banana's own x and y.
 */
export function hitsGorilla(px, py, p, idx) {
  const q = gorillaPose(p, idx);
  const s = q.shape;

  // Into body-local space — the same frame drawGorilla() works in.
  const dx = px - p.x - q.jitter;
  const dy = py - p.y - q.lift;
  const sign = idx === 0 ? -1 : 1;

  // Head, plus the ears where the species has them.
  const hx = dx - q.head.x;
  const hy = dy - (s.headY + q.bob + q.headBob + q.head.y);
  if ((hx * hx) / (s.headRx + HIT_SLACK) ** 2 + (hy * hy) / (s.headRy + HIT_SLACK) ** 2 <= 1) return true;

  const ear = s.earR + HIT_SLACK;
  if (Math.hypot(hx - s.earDx, hy - s.earY) <= ear || Math.hypot(hx + s.earDx, hy - s.earY) <= ear) return true;

  // Torso, rounded exactly the way rr() draws it. The width steps down below the
  // TORSO, not at legTopY: the legs start inside the torso, so a step at legTopY
  // left the hips of a wide-chested species — an orangutan's chest is 21 half-wide
  // against legs of 18 — three columns wider than anything a banana could hit.
  if (inRoundedRect(dx, dy, s.torsoHalf + HIT_SLACK, s.torsoTop + q.bob - HIT_SLACK,
                    s.torsoBottom + HIT_SLACK, s.torsoR + HIT_SLACK)) return true;

  // Legs and feet, taken as the one block they are drawn as. Not rounded: the feet
  // underneath are square, so rounding here would cut a corner off the shadow's
  // neighbours that a banana could still visibly land on.
  if (Math.abs(dx) <= s.legsHalf + HIT_SLACK
      && dy >= s.legTopY - HIT_SLACK && dy <= s.feetY + HIT_SLACK) return true;

  // Both arms, shoulder to wherever the hand has got to.
  const shoulderY = s.shoulderY + q.bob;
  const r = s.armW / 2 + HIT_SLACK;
  const arms = [
    [sign * s.shoulderDx, shoulderY, q.free.x, q.free.y],
    [-sign * s.shoulderDx, shoulderY, q.thrown.x, q.thrown.y],
  ];
  if (arms.some(([ax, ay, bx, by]) => distToSegment(dx, dy, ax, ay, bx, by) <= r)) return true;

  // And everything it is wearing or growing out of its face, from the same boxes
  // the drawing fills in. Accessories hang off the head, so they are measured
  // from it.
  const dir = facingOf(idx);
  const wear = q.worn.flatMap(a => a.parts).concat(s.snout || []);

  return wear.some(part => inPart(dx - q.head.x, dy - (s.headY + q.bob + q.headBob + q.head.y), part, dir));
}

/**
 * The rectangle a raised shield occupies, in canvas coordinates.
 *
 * The drawing and the test that stops a banana both come through here, for the
 * same reason the fist and the hitbox do: a shield you cannot see, or a visible
 * shield that shots sail through, is worse than no shield at all. It stands in
 * front of him, so the head and the back stay open — which is the answer to it.
 */
export function gorillaShieldRect(p, idx) {
  const dir = facingOf(idx);

  return {
    // Held by the point: the tip is in the fist, in both axes, and the shield rises from
    // there. It used to stand on the hip with its whole width in front of the hand, which
    // read as a door propped up beside him. The rectangle is what the banana test uses, so
    // this is also what he is protected by — a shorter shield covers less, and the gap the
    // hand leaves under the point is now a gap.
    x: p.x + dir * FIST_DX - SHIELD_W / 2,
    y: p.y + FIST_DY - SHIELD_H,
    w: SHIELD_W,
    h: SHIELD_H,
  };
}

/**
 * The shield, painted on exactly the rectangle the test uses.
 *
 * A shield and not a door: shoulders square at the top, and the bottom SHIELD_POINT pixels
 * drawn in to a point. The point is the whole of what says which way up it is being held —
 * a rectangle with a cross on it reads as a placard, which is what a player said it looked
 * like. It is drawn INSIDE the rectangle the test uses, so the silhouette narrows and the
 * armour does not: what a shield blocks is unchanged by a pixel.
 */
export function drawShield(p, idx) {
  const r = gorillaShieldRect(p, idx);

  const point = Math.min(SHIELD_POINT, r.h - 4);
  const body = r.h - point;

  pixelRect(r.x, r.y, r.w, body, '#7c8798');

  // The point, a row at a time, rather than as a path: the rest of this art is drawn on
  // whole pixels and an anti-aliased edge here would be the one soft thing on the figure.
  for (let i = 0; i < point; i++) {
    const w = Math.max(1, Math.round(r.w * (point - i) / point));
    pixelRect(r.x + (r.w - w) / 2, r.y + body + i, w, 1, '#7c8798');
  }

  pixelRect(r.x, r.y, r.w, 3, '#aab4c4');

  // A boss and a cross — and the cross stops where the shield starts to close, because one
  // that ran on into the point would stick out of the silhouette.
  pixelRect(r.x + (r.w - 3) / 2, r.y + 6, 3, body - 8, '#5c6675');
  pixelRect(r.x + 3, r.y + Math.round(body / 2) - 1, r.w - 6, 3, '#5c6675');
}

/**
 * Where the spring is planted: under his own feet, on the roof.
 *
 * It is measured on the pixels it is painted on, exactly like the shield's, and the
 * rectangle it is tested with is the one it is drawn in. It used to stand in front of him
 * at SPRING_DX — a fence — and it is under him now because a spring you stand on is a spring
 * you dance on, and because the two of them have to be in the same place: what you see is
 * what a banana can hit, and a gorilla bobbing on a spring he is not standing on would be
 * the drawing and the hit test disagreeing in the one place the player is looking.
 */
export function gorillaSpringRect(p, idx) {
  const s = speciesFor(p, idx).shape;
  const ground = p.y + s.shadowY;
  const ride = springRide(idx);
  // The rectangle follows the coil as it is drawn this frame — stretched taller and leaning to
  // one side — because a banana that lands on the visible spring has to bounce off it, and the
  // leaning spring is a wider thing than the standing one. What you see is what you can hit,
  // applied to a bit of scenery that moves.
  const top = ground - SPRING_H - ride.stretch;
  const lean = ride.sway;

  return {
    x: p.x - SPRING_W / 2 + Math.min(0, lean),
    y: top,
    w: SPRING_W + Math.abs(lean),
    h: ground - top,
  };
}

/**
 * The spring: a base on the roof, a coil that stretches and folds, and the plate his feet are on.
 *
 * The plate is the thing that matters — the coil is drawn up to it — so the gorilla sits on the
 * spring instead of hovering over it. The bands sit at fixed fractions of the coil rather than at
 * fixed pixels from the top, which is what lets the coil be seen stretching: the same three bands
 * spread apart as the plate goes up and bunch together as it comes back down.
 */
function drawSpring(p, idx) {
  const s = speciesFor(p, idx).shape;
  const ground = Math.round(p.y + s.shadowY);
  const ride = springRide(idx);
  const top = Math.round(ground - SPRING_H - ride.stretch);
  const baseX = Math.round(p.x - SPRING_W / 2);
  const span = ground - 3 - top;
  // Each band further over than the one below it, the top one exactly over the plate: the coil
  // bends rather than tilts, and the thing at the end of it is where the gorilla is.
  const at = f => Math.round(ride.sway * f);

  // The base, wide and plumb on the roof.
  pixelRect(baseX, ground - 3, SPRING_W, 3, '#59637a');
  // The coil, suggested with two bands: a full spiral at nine pixels tall turns into a grey smear.
  pixelRect(baseX + 6 + at(0.45), Math.round(top + span * 0.62), SPRING_W - 12, 2, '#6d7791');
  pixelRect(baseX + 3 + at(0.75), Math.round(top + span * 0.28), SPRING_W - 6, 2, '#8b95a8');
  // And the plate, under his feet.
  pixelRect(baseX + 3 + at(1), top, SPRING_W - 6, 3, '#c8d2e2');
}

/**
 * The mushroom, parked above whoever is holding it.
 *
 * It has to be visible, and this is not decoration: a mushroom means you can be
 * hit and live, which is a rule the other player can only play around if they can
 * see that it is there. Same reason the shield is drawn where it is measured.
 */
function drawMushroom(p, idx) {
  const head = gorillaHead(p, idx);
  const x = Math.round(head.x);
  const y = Math.round(head.y - head.ry - 16);

  pixelRect(x - 1, y + 4, 3, 5, '#e8dcc0');
  pixelRect(x - 6, y, 13, 5, '#d8483c');
  pixelRect(x - 4, y - 2, 9, 3, '#d8483c');
  pixelRect(x - 4, y + 1, 2, 2, '#f6f0e2');
  pixelRect(x + 2, y + 1, 2, 2, '#f6f0e2');
}

/**
 * Whether this side is gold this frame: the moment after a force field took a hit for him.
 *
 * Read off the clock instead of being turned off by a timer, the same way every other timed
 * thing in the game is done, so a pause, a lost frame or a round thrown away mid-flash
 * cannot leave him like that for good.
 */
function goldRightNow(idx) {
  const glow = state.saiyanGlow;
  return !!glow && glow.idx === idx && state.clock < glow.until;
}

/**
 * The force field: an oval around the whole gorilla, painted over him and bigger than he is.
 *
 * Over him and unmistakable, because it is the one thing in the game that survives a direct
 * hit: whoever is throwing at him has to be able to see that this shot is not going to land
 * BEFORE spending the turn on it. Exactly the rule the mushroom is drawn under, one size up.
 *
 * It has no rectangle of its own and no test of its own, which is the design and not an
 * omission: nothing has to hit the field, because the field is what happens when a banana
 * reaches the gorilla at all. See the hit resolution in flow.js.
 */
function drawForceField(p, idx) {
  const q = gorillaPose(p, idx);
  const s = q.shape;
  const cx = Math.round(p.x + q.jitter);
  const cy = Math.round(p.y + q.lift);
  const top = cy + s.headY + q.headBob + q.head.y - s.headRy - 9;
  const bottom = p.y + s.shadowY + 4;
  const rx = Math.max(s.torsoHalf, s.headRx, s.earDx + s.earR) + 13;
  const ry = Math.max(12, (bottom - top) / 2);
  const midY = (top + bottom) / 2;

  ctx.save();
  ctx.beginPath();
  ctx.ellipse(cx, midY, rx, ry, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,224,122,.10)';
  ctx.fill();
  ctx.strokeStyle = '#ffe07a';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Three sparks on the rim, so it reads as a field rather than as a hoop somebody is
  // standing inside. Fixed spots, so they do not shimmer into noise at this size.
  ctx.fillStyle = '#fff3c4';
  [[-1, -0.5], [0.86, -0.06], [-0.7, 0.62]].forEach(([dx, dy]) => {
    ctx.fillRect(Math.round(cx + dx * rx) - 2, Math.round(midY + dy * ry) - 1, 4, 2);
  });
  ctx.restore();
}

/**
 * Everything either side is carrying or standing behind, painted after the
 * gorillas because all of it is in front of them.
 */
export function drawProps() {
  const raised = state.shield;
  if (raised) {
    const p = state.players[raised.idx];
    if (p && p.alive) drawShield(p, raised.idx);
  }

  const planted = state.spring;
  if (planted) {
    const p = state.players[planted.idx];
    if (p && p.alive) drawSpring(p, planted.idx);
  }

  const kept = state.mushroom;
  if (kept) {
    const p = state.players[kept.idx];
    if (p && p.alive) drawMushroom(p, kept.idx);
  }

  const field = state.saiyan;
  if (field) {
    const p = state.players[field.idx];
    if (p && p.alive) drawForceField(p, field.idx);
  }
}

// Classic silhouette gorilla: stocky body, round head with small ears, arms
// hanging past the hips.
export function drawGorilla(p, idx) {
  if (!p.alive) return;
  // A duel whose invitation has not been answered. There is nobody on that side yet, and a
  // gorilla standing there would be wearing whatever this machine happens to have saved for
  // player two — a character nobody chose, in a match nobody has joined. It appears when their
  // first code does, dressed as they are dressed.
  if (state.awaitingRival && idx === 1) return;

  const q = gorillaPose(p, idx);

  // Only the drawn pieces move, and hitsGorilla() reads the same pose, so what
  // you can see is exactly what a banana can hit.
  const cx = Math.round(p.x + q.jitter);
  // `lift` raises the whole animal, feet and all, which is what a jump is.
  const cy = Math.round(p.y + q.lift);
  const s = q.shape;
  const { body: fur, dark: furD, belly, face } = q.species;

  const shoulderY = cy + s.shoulderY + q.bob;
  const headX = cx + q.head.x;
  const headY = cy + s.headY + q.bob + q.headBob + q.head.y;

  // Where each hand goes. Moving a hand drags the whole arm with it, because the
  // arms are stroked from the shoulder out to the fist.
  const freeX = cx + q.free.x;
  const freeY = cy + q.free.y;
  const throwX = cx + q.thrown.x;
  const throwY = cy + q.thrown.y;

  // shadow on the rooftop — stays put while the body breathes, and when the
  // whole character leaves the ground in a fright
  pixelRect(cx - s.shadowW / 2, Math.round(p.y) + s.shadowY, s.shadowW, 4, '#080b15');

  // And the light he is standing in, when the field has just taken a hit for him. Behind the
  // body on purpose: what it has to say is "that did not touch him", and a gorilla glowing at
  // the edges says it without the gold hair being the only clue.
  if (goldRightNow(idx)) {
    ctx.save();
    // Light, not paint. At .32 the aura washed the whole animal gold and took the point off
    // the one thing the transformation is about — the hair — so what was left was a pale
    // gorilla rather than a gorilla whose hair had changed colour. Seen at 1:1 in a crop.
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = '#ffe07a';
    ctx.beginPath();
    ctx.ellipse(cx, cy + (s.torsoTop + s.torsoBottom) / 2, s.torsoHalf + 10, (s.torsoBottom - s.torsoTop) / 2 + 13, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(headX, headY, s.headRx + 8, s.headRy + 8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // legs, short and bent (classic gorillas.bas stance)
  ctx.fillStyle = furD;
  rr(cx - s.legOffset, cy + s.legTopY, s.legW, s.legH, 4);
  rr(cx + s.legGap, cy + s.legTopY, s.legW, s.legH, 4);
  pixelRect(cx - s.legOffset - 1, cy + s.footY, s.footW, s.footH, '#241a12');
  pixelRect(cx + s.legGap - 1, cy + s.footY, s.footW, s.footH, '#241a12');

  // stocky torso
  ctx.fillStyle = fur;
  rr(cx - s.torsoHalf, cy + s.torsoTop + q.bob, s.torsoHalf * 2, s.torsoBottom - s.torsoTop, s.torsoR);
  ctx.fillStyle = belly;
  rr(cx - s.bellyX, cy + s.bellyY + q.bob, s.bellyW, s.bellyH, s.bellyR);

  // round head with ears — the ears sit where the species puts them, which for
  // a panda is on top and for a baboon is barely there at all
  ctx.fillStyle = fur;
  ctx.beginPath();
  ctx.ellipse(headX - s.earDx, headY + s.earY, s.earR, s.earR, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(headX + s.earDx, headY + s.earY, s.earR, s.earR, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(headX, headY, s.headRx, s.headRy, 0, 0, Math.PI * 2);
  ctx.fill();

  // A scruffy fringe. Sized off the head rather than pinned to fixed offsets: the
  // tufts have to stay inside the ellipse the hit test uses, or they are fur a
  // banana can sail through. Written as fractions of the head they stay inside
  // for every species, tall head or wide head.
  if (s.shaggy) {
    const fx = s.headRx * 0.5;
    const fy = s.headRy * 0.6;
    const rx = s.headRx * 0.3;
    const ry = s.headRy * 0.24;

    ctx.fillStyle = belly;
    [[-fx, -fy], [0, -s.headRy * 0.78], [fx, -fy]].forEach(([tx, ty]) => {
      ctx.beginPath();
      ctx.ellipse(headX + tx, headY + ty, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  // The hair, when he is gold: a cap over the skull and three tufts standing in it, all of
  // it clipped to the ellipse the hit test uses. Hair drawn outside that ellipse would be
  // fur a banana can sail through, which is the one rule this art has kept everywhere else —
  // and a super warrior's spikes are the most tempting thing in the game to draw past it.
  if (goldRightNow(idx)) {
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(headX, headY, s.headRx, s.headRy, 0, 0, Math.PI * 2);
    ctx.clip();

    ctx.fillStyle = '#ffe07a';
    ctx.fillRect(headX - s.headRx, headY - s.headRy, s.headRx * 2, Math.ceil(s.headRy * 0.7));

    // The tufts, in the darker gold: it is what makes it hair rather than a cap, and the
    // clip turns each triangle into a spike once it reaches the crown.
    ctx.fillStyle = '#d9a13c';
    for (let i = -1; i <= 1; i++) {
      const tx = Math.round(headX + i * s.headRx * 0.44);
      const tall = s.headRy * (i === 0 ? 1.3 : 1);
      ctx.beginPath();
      ctx.moveTo(tx - 3, headY - s.headRy * 0.15);
      ctx.lineTo(tx, headY - s.headRy * 0.15 - tall);
      ctx.lineTo(tx + 3, headY - s.headRy * 0.15);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  // muzzle, with an open mouth when the pose calls for one
  ctx.fillStyle = face;
  ctx.beginPath();
  ctx.ellipse(headX, headY + s.muzzleY, s.muzzleRx, s.muzzleRy, 0, 0, Math.PI * 2);
  ctx.fill();

  if (q.mouth > 0) {
    ctx.fillStyle = INK;
    ctx.beginPath();
    ctx.ellipse(headX, headY + s.muzzleY + 2, 4, 0.6 + q.mouth * 3.6, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // nose sits on top of the mouth, so an open jaw reads as being under it
  pixelRect(headX - 2, headY + s.browY, 4, 2, INK);
  drawEyes(headX, headY, q.eyes || (q.blink ? 'blink' : 'open'));
  if (q.zzz) drawZzz(headX, headY);

  // Hats, glasses and scarves, from the very boxes hitsGorilla() tests against,
  // and before the arms so a hand scratching its head goes over the hat.
  paintExtra(q, headX, headY, idx);

  // The arms go on last. Drawn underneath, a hand resting on the belly or
  // rubbing the face vanishes behind it — which is what most antics ask of it.
  ctx.strokeStyle = fur;
  ctx.lineWidth = s.armW;
  ctx.lineCap = 'round';

  // free arm, from its shoulder down to wherever the hand has wandered
  ctx.beginPath();
  ctx.moveTo(cx + (idx ? s.shoulderDx : -s.shoulderDx), shoulderY);
  ctx.lineTo(freeX, freeY);
  ctx.stroke();

  // throwing arm, raised toward the target
  ctx.beginPath();
  ctx.moveTo(cx + (idx ? -s.shoulderDx : s.shoulderDx), shoulderY);
  ctx.lineTo(throwX, throwY);
  ctx.stroke();

  // hands
  ctx.fillStyle = furD;
  ctx.beginPath();
  ctx.ellipse(freeX, freeY + 2, s.handR, s.handR, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(throwX, throwY, s.handR, s.handR, 0, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Whatever this side is wearing, plus anything the species grows out of its face.
 * Both come from lists that the hit test reads too — the drawing is not allowed
 * to be anywhere the hitbox is not.
 */
function paintExtra(q, headX, headY, idx) {
  const dir = facingOf(idx);

  q.worn
    .flatMap(a => a.parts)
    .concat(q.shape.snout || [])
    .forEach(part => {
      const r = partRect(part, headX, headY, dir);
      pixelRect(r.x, r.y, r.w, r.h, r.c);
    });
}

/** Eyes are three pixels of ink; the shape is the whole expression. */
function drawEyes(x, y, look) {
  if (look === 'blink' || look === 'shut') {
    pixelRect(x - 6, y - 1, 4, 1, INK);
    pixelRect(x + 3, y - 1, 4, 1, INK);
    return;
  }

  if (look === 'wide') {
    pixelRect(x - 7, y - 3, 4, 4, INK);
    pixelRect(x + 3, y - 3, 4, 4, INK);
    return;
  }

  pixelRect(x - 6, y - 2, 3, 3, INK);
  pixelRect(x + 3, y - 2, 3, 3, INK);
}

/** Three little z's drifting off a sleeping gorilla. */
function drawZzz(x, y) {
  ctx.save();
  ctx.fillStyle = '#cfe9ff';
  ctx.textAlign = 'center';

  for (let i = 0; i < 3; i++) {
    const rise = ((state.clock / 1600) + i / 3) % 1;
    ctx.globalAlpha = 0.75 * (1 - rise);
    ctx.font = `bold ${8 + i * 2}px monospace`;
    ctx.fillText('z', x + 16 + i * 5, y - 14 - rise * 20 - i * 5);
  }

  ctx.restore();
}

/**
 * The gorillas' idle motion: a slow breath through the torso and head, a small
 * sway on the free arm, and the odd blink.
 *
 * Offsets are rounded to whole pixels on purpose — the art is pixel-aligned, so
 * sub-pixel drift would only smear it. The two gorillas run out of phase so they
 * never breathe in lockstep.
 */
function idleMotion(idx) {
  const phase = idx * 1.7;
  const cycle = (state.clock / 1000) * BREATH_HZ * Math.PI * 2;

  return {
    bob: Math.round(Math.sin(cycle + phase)),
    // The head trails the torso slightly, which reads as weight.
    head: Math.round(Math.sin(cycle + phase - 0.6)),
    sway: Math.round(Math.sin(cycle + phase + 0.3)),
    blink: (state.clock + (idx * BLINK_PERIOD_MS) / 2) % BLINK_PERIOD_MS < BLINK_MS,
  };
}
